import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../api";

// „Beim Öffnen als gelesen markieren" (F-06) — an/aus.
//
// Warum es den Schalter gibt: Atlas markiert eine Mail beim Öffnen im
// Posteingang sofort als gelesen und schreibt das AKTIV nach Graph zurück
// (Mail.tsx `select()` → `markRead` → PATCH /mail/messages/{imid}/read →
// app/routers/mail.py → email-service → Graph). Wer in Outlook „nicht
// automatisch als gelesen" eingestellt hat, bekam durch Atlas trotzdem
// gelesene Mails.
//
// Ausschalten ENTKOPPELT NICHTS: gelesen/ungelesen bleibt genau eine Wahrheit
// in Graph, die Atlas weiterhin live liest und die über den Lese-Punkt in der
// Zeile jederzeit von Hand gesetzt werden kann. Aus heisst nur: beim blossen
// Öffnen geht kein Schreibimpuls raus.
//
// Bewusst BINÄR und nicht „sofort / nach N Sekunden / nie": eine Verweildauer
// wäre eine zweite, unsichtbare Regel, die niemand nachvollziehen kann, wenn
// eine Mail mal gelesen ist und mal nicht.
//
// Default AN = das bisherige Verhalten; für einen Bestandsnutzer ändert sich
// dadurch nichts.
//
// PERSISTENZ: server-seitig am NUTZER (`user_settings.payload.mail_auto_mark_read`
// via GET/PUT /settings), NICHT im localStorage — derselbe Weg wie
// `mail_inbox_depth`. Es ist eine Einstellung des Nutzers, keine des Browsers:
// wer sie am Laptop ausschaltet, will sie auch am zweiten Gerät aus haben.
export const DEFAULT_AUTO_MARK_READ = true;

/** Nur das Feld, das uns interessiert — der Rest von SettingsOut ist egal. */
interface AutoMarkReadSettingsResponse {
  mail_auto_mark_read?: unknown;
}

export interface UseAutoMarkRead {
  /** true = Öffnen markiert als gelesen (Default). */
  autoMarkRead: boolean;
  /** false, solange die Nutzer-Einstellung noch geladen wird. Wer daraus eine
   *  Schreib-Entscheidung ableitet, wartet darauf — sonst würde die erste
   *  geöffnete Mail nach dem Default behandelt und der Schalter wirkte
   *  sporadisch. */
  ready: boolean;
  saving: boolean;
  /** Optimistisch: setzt sofort + schreibt via PUT /settings. Bei Fehlschlag
   *  wird zurückgerollt. Liefert, ob gespeichert wurde. */
  setAutoMarkRead: (v: boolean) => Promise<boolean>;
}

export function useAutoMarkRead(): UseAutoMarkRead {
  const [autoMarkRead, setRaw] = useState<boolean>(DEFAULT_AUTO_MARK_READ);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    void api
      .get<AutoMarkReadSettingsResponse>("/settings")
      .then((res) => {
        if (!aliveRef.current) return;
        // Defensiv: fehlendes/kaputtes Feld (z.B. älteres Backend) darf die
        // Seite NICHT mitreißen — dann gilt schlicht der Default. Nur ein
        // echter Boolean zählt; `!!res.mail_auto_mark_read` würde aus einem
        // 0/""/null im JSONB ein „aus" erfinden.
        const v = res?.mail_auto_mark_read;
        if (typeof v === "boolean") setRaw(v);
      })
      .catch(() => {
        /* Settings nicht lesbar → Default. Kein Toast: die Mail-Liste soll
           laden, nicht wegen einer Präferenz blockieren. */
      })
      .finally(() => {
        if (aliveRef.current) setReady(true);
      });
    return () => {
      aliveRef.current = false;
    };
  }, []);

  const setAutoMarkRead = useCallback(async (v: boolean): Promise<boolean> => {
    let prev: boolean = DEFAULT_AUTO_MARK_READ;
    setRaw((cur) => {
      prev = cur;
      return v;
    });
    setSaving(true);
    try {
      // Partieller PUT: der Server merged nur dieses Feld (andere
      // Einstellungen bleiben unberührt).
      await api.put("/settings", { mail_auto_mark_read: v });
      return true;
    } catch {
      if (aliveRef.current) setRaw(prev);
      return false;
    } finally {
      if (aliveRef.current) setSaving(false);
    }
  }, []);

  return { autoMarkRead, ready, saving, setAutoMarkRead };
}
